import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { Command } from 'cmdk'
import { ChevronRight, Search } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'
import { Button } from './button'
import { Kbd } from './kbd'
import { ReportCard, type ReportCardData } from './report-card'
import { type ResultsTableData, ResultsTables } from './results-table'
import { type Citation, CitationChips, RileyMark } from './riley-rail'
import { TraceLine, type TraceLineProps } from './trace-line'

export type PaletteMode = 'command' | 'search' | 'ask'

/** Mode is decided by the first characters (scope §5.0): `>` or a trailing `?` asks Riley; `#`/`@` search. */
export function detectPaletteMode(input: string): PaletteMode {
  const s = input.trim()
  if (s.startsWith('>') || (s.length > 1 && s.endsWith('?'))) return 'ask'
  if (s.startsWith('#') || s.startsWith('@')) return 'search'
  return 'command'
}

export function stripModePrefix(input: string): string {
  return input.trim().replace(/^[>#@]\s*/, '')
}

export interface PaletteCommand {
  id: string
  label: string
  group?: string
  icon?: React.ReactNode
  keys?: string
  keywords?: string[]
  onSelect: () => void
}

export interface PaletteSearchItem {
  id: string
  kind: 'case' | 'ticket' | 'customer' | 'contractor' | 'person' | 'knowledge' | 'account' | 'contact'
  label: string
  detail?: string
  /** Short state, e.g. "Open · Reply owed" / "Closed" / a Desk status. */
  status?: string
  closed?: boolean
  /** #876 "New reply · 5 min ago": a reply this person has not seen (bold label and a dot). */
  unread?: string
  onSelect: () => void
}

/**
 * Server search results for the current query. `error` is rendered as "Search unreachable", never as
 * "nothing matches" (absence is not evidence, P11).
 */
export interface PaletteRemoteSearch {
  status: 'idle' | 'loading' | 'ok' | 'error'
  items: PaletteSearchItem[]
  message?: string
}

export interface AskResult {
  trace: TraceLineProps[]
  finding: string
  citations?: Citation[]
  resultTables?: ResultsTableData[]
  reportCards?: ReportCardData[]
  actions: { label: string; primary?: boolean; onSelect: () => void }[]
}

function matches(q: string, ...fields: (string | undefined)[]): boolean {
  if (!q) return true
  const needle = q.toLowerCase()
  return fields.some((f) => f?.toLowerCase().includes(needle))
}

/**
 * Ctrl+K palette (mockup 06). Command mode lists actions scoped to the screen with their chords;
 * Search finds cases, tickets, customers, contractors, people; Ask sends the line to Riley and
 * shows the investigation with actions. The footer names the scope so nothing lands on the wrong case.
 */
export function CommandPalette({
  open,
  onOpenChange,
  commands,
  searchItems,
  onAsk,
  scope,
  remote,
  onQueryChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  commands: PaletteCommand[]
  searchItems: PaletteSearchItem[]
  onAsk?: (question: string) => Promise<AskResult> | AskResult
  /** "in C-0412 · Harbourfront Grand Hotel" */
  scope?: string
  /** Server hits for the current query (debounced by the caller). */
  remote?: PaletteRemoteSearch
  /** Called with the query text without its mode prefix, and the mode. */
  onQueryChange?: (query: string, mode: PaletteMode) => void
}) {
  const [value, setValue] = React.useState('')
  const [asked, setAsked] = React.useState<{ question: string; result: AskResult | null; shown: number } | null>(null)
  const mode = detectPaletteMode(value)
  const query = stripModePrefix(value)

  React.useEffect(() => {
    onQueryChange?.(query, mode)
  }, [onQueryChange, query, mode])

  React.useEffect(() => {
    if (!open) {
      setValue('')
      setAsked(null)
    }
  }, [open])

  // Reveal trace lines one at a time so the investigation reads as it runs.
  React.useEffect(() => {
    if (!asked?.result || asked.shown >= asked.result.trace.length) return
    const t = setTimeout(() => setAsked((a) => (a ? { ...a, shown: a.shown + 1 } : a)), 180)
    return () => clearTimeout(t)
  }, [asked])

  const ask = async (question: string) => {
    if (!onAsk) return
    setAsked({ question, result: null, shown: 0 })
    const result = await onAsk(question)
    setAsked({ question, result, shown: 0 })
  }

  // Close first, then run: the palette always closes, even when the pick is the page already open or the action throws.
  const run = (fn: () => void) => {
    onOpenChange(false)
    fn()
  }

  const cmds = mode === 'command' ? commands.filter((c) => matches(query, c.label, ...(c.keywords ?? []))) : []
  const localHits = mode !== 'ask' && query ? searchItems.filter((s) => matches(query, s.label, s.detail, s.id)) : []
  const seen = new Set(localHits.map((h) => `${h.kind}-${h.id}`))
  const remoteActive = mode !== 'ask' && Boolean(query) && remote !== undefined
  const remoteHits = remoteActive && remote?.status === 'ok' ? remote.items.filter((h) => !seen.has(`${h.kind}-${h.id}`)) : []
  const hits = [...localHits, ...remoteHits]
  const remotePending = remoteActive && (remote?.status === 'loading' || remote?.status === 'error')
  const groups = [...new Set(cmds.map((c) => c.group ?? 'Actions'))]

  // Enter always runs the top row: keep cmdk's selection on the first rendered item as results change (commands, then
  // local hits, then server hits that arrive later), unless the person has moved the selection with the arrow keys.
  const firstValue =
    mode === 'ask'
      ? ''
      : ((groups.length ? cmds.find((c) => (c.group ?? 'Actions') === groups[0])?.id : undefined) ??
        (hits[0] ? `${hits[0].kind}-${hits[0].id}` : ''))
  const [selected, setSelected] = React.useState('')
  const moved = React.useRef(false)
  React.useEffect(() => {
    if (!moved.current) setSelected(firstValue)
  }, [firstValue])

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-50 bg-foreground/25 transition-opacity duration-(--duration-fast) data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <DialogPrimitive.Popup
          aria-label="Command palette"
          data-slot="dialog-popup"
          className="fixed top-[12vh] left-1/2 z-50 w-[min(680px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg outline-none transition-[opacity,transform] duration-(--duration-fast) data-[ending-style]:scale-[0.98] data-[ending-style]:opacity-0 data-[starting-style]:scale-[0.98] data-[starting-style]:opacity-0 max-pane:!inset-0 max-pane:!top-0 max-pane:!left-0 max-pane:!flex max-pane:!h-dvh max-pane:!w-full max-pane:!max-w-none max-pane:!translate-x-0 max-pane:!translate-y-0 max-pane:flex-col max-pane:rounded-none"
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <Command
            shouldFilter={false}
            loop
            label="Command palette"
            className="flex max-h-[70vh] flex-col max-pane:h-full max-pane:max-h-none max-pane:min-h-0"
            value={selected}
            onValueChange={setSelected}
          >
            <div className="flex items-center gap-2 border-b border-border px-4">
              {mode === 'ask' ? <RileyMark className="size-5" /> : <Search className="size-5 text-subtle" />}
              {mode === 'ask' ? <span className="text-xs font-medium text-riley">Ask Riley</span> : null}
              <Command.Input
                autoFocus
                value={value}
                onValueChange={(v) => {
                  moved.current = false // a new query snaps the selection back to the top row
                  setValue(v)
                  setAsked(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') moved.current = true
                  if (e.key === 'Enter' && mode === 'ask' && query) {
                    e.preventDefault()
                    void ask(query)
                  }
                }}
                placeholder="Type a command, # ticket, @ person, or > ask Riley"
                className="h-13 flex-1 bg-transparent text-lg outline-none placeholder:text-subtle"
              />
              <Kbd keys="esc" />
            </div>

            <Command.List className="min-h-0 flex-1 overflow-y-auto p-2">
              {mode === 'ask' ? (
                asked ? (
                  <AskPanel asked={asked} onAction={run} />
                ) : (
                  <Command.Item
                    value="ask"
                    onSelect={() => void ask(query)}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-base data-[selected=true]:bg-riley-soft"
                  >
                    <RileyMark className="size-4" />
                    <span className="flex-1">
                      Ask Riley: <span className="font-medium">{query || '…'}</span>
                    </span>
                    <Kbd keys="enter" />
                  </Command.Item>
                )
              ) : (
                <>
                  {remotePending ? null : (
                    <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
                      Nothing matches. End with <Kbd keys="?" /> or start with <Kbd keys=">" /> to ask Riley.
                    </Command.Empty>
                  )}
                  {groups.map((g) => (
                    <Command.Group
                      key={g}
                      heading={g}
                      className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-subtle"
                    >
                      {cmds
                        .filter((c) => (c.group ?? 'Actions') === g)
                        .map((c) => (
                          <Command.Item
                            key={c.id}
                            value={c.id}
                            onSelect={() => run(c.onSelect)}
                            className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-base data-[selected=true]:bg-accent [&_svg]:size-4 [&_svg]:text-muted-foreground"
                          >
                            {c.icon}
                            <span className="flex-1">{c.label}</span>
                            {c.keys ? <Kbd keys={c.keys} /> : null}
                          </Command.Item>
                        ))}
                    </Command.Group>
                  ))}
                  {hits.length ? (
                    <Command.Group
                      heading="Search"
                      className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-subtle"
                    >
                      {hits.slice(0, 8).map((h) => (
                        <Command.Item
                          key={`${h.kind}-${h.id}`}
                          value={`${h.kind}-${h.id}`}
                          onSelect={() => run(h.onSelect)}
                          className="grid cursor-pointer grid-cols-[4rem_minmax(0,1fr)_auto_1rem] items-center gap-3 rounded-md px-3 py-2 text-left data-[selected=true]:bg-accent"
                        >
                          <span className="self-start pt-0.5 text-xs text-subtle">{h.kind}</span>
                          <span className="flex min-w-0 flex-col text-left">
                            <span
                              className={h.unread ? 'truncate font-bold' : 'truncate font-medium'}
                              data-unread={h.unread ? 'true' : undefined}
                            >
                              {h.unread ? (
                                <span aria-hidden className="mr-1.5 inline-block size-2 rounded-full bg-primary align-middle" />
                              ) : null}
                              {h.label}
                              {h.unread ? <span className="ml-2 text-xs font-semibold text-primary">{h.unread}</span> : null}
                            </span>
                            {h.detail ? <span className="truncate text-xs text-muted-foreground">{h.detail}</span> : null}
                          </span>
                          {h.status ? (
                            <span
                              data-closed={h.closed ? 'true' : 'false'}
                              className={
                                h.closed
                                  ? 'whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-xs text-subtle'
                                  : 'whitespace-nowrap rounded-full bg-accent px-2 py-0.5 text-xs font-medium'
                              }
                            >
                              {h.status}
                            </span>
                          ) : (
                            <span />
                          )}
                          <ChevronRight className="size-4 text-subtle" />
                        </Command.Item>
                      ))}
                    </Command.Group>
                  ) : null}
                  {remoteActive && remote?.status === 'loading' ? (
                    <p role="status" className="px-3 py-2 text-sm text-subtle">
                      Searching…
                    </p>
                  ) : null}
                  {remoteActive && remote?.status === 'error' ? (
                    <p role="status" data-search="unreachable" className="px-3 py-2 text-sm text-warning">
                      Search unreachable{remote.message ? `: ${remote.message}` : ''}. Results above are from this screen only.
                    </p>
                  ) : null}
                </>
              )}
            </Command.List>

            <div className="flex items-center gap-3 border-t border-border bg-canvas px-4 py-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{scope ?? 'Everywhere'}</span>
              <span className="ml-auto flex items-center gap-1">
                <Kbd keys=">" /> ask
              </span>
              <span className="flex items-center gap-1">
                <Kbd keys="#" /> ticket
              </span>
              <span className="flex items-center gap-1">
                <Kbd keys="@" /> person
              </span>
            </div>
          </Command>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function AskPanel({
  asked,
  onAction,
}: {
  asked: { question: string; result: AskResult | null; shown: number }
  onAction: (fn: () => void) => void
}) {
  const r = asked.result
  const done = r !== null && asked.shown >= r.trace.length
  return (
    <div className="space-y-3 p-2" aria-live="polite">
      <p className="text-xl">
        <span className="text-subtle">&gt;</span> {asked.question}
      </p>
      <div className="rounded-lg bg-muted/70 p-3">
        <p className={cn('mb-1 text-sm font-medium', done ? 'text-foreground' : 'text-riley')}>
          {done ? 'Investigated' : 'Investigating…'}
        </p>
        {r?.trace.slice(0, asked.shown).map((t) => (
          <TraceLine key={`${t.source}-${t.detail}`} {...t} />
        ))}
        {done ? <p className="mt-2 text-base">{r.finding}</p> : null}
        {done ? <ResultsTables tables={r.resultTables} /> : null}
        {done && r.reportCards?.length ? (
          <div className="space-y-3">
            {r.reportCards.map((report) => (
              <ReportCard key={`${report.report_type}:${report.generated_at}`} report={report} />
            ))}
          </div>
        ) : null}
        {done && r.citations?.length ? <CitationChips citations={r.citations} /> : null}
      </div>
      {done ? (
        <div className="flex flex-wrap gap-2">
          {r.actions.map((a) => (
            <Button key={a.label} variant={a.primary ? 'primary' : 'outline'} size="lg" onClick={() => onAction(a.onSelect)}>
              {a.label}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
